/**
 * Where a broken designer expression goes when it is nobody's fault but the
 * design's.
 *
 * A module-level sink rather than a parameter threaded through four call sites:
 * the sandbox is reached from visibility, range checks, defaults and
 * computations, none of which have — or should have — a route to preview UI.
 * It mirrors how telemetry already leaves this layer.
 *
 * Nothing is installed by default, so the live patient flow behaves exactly as
 * before: a fault is still tracked, and a participant is still shown nothing.
 * Only the preview installs a sink.
 */

let sink = null;

/**
 * Installs the sink and returns a function that removes it.
 *
 * The returned remover only clears the sink if it is still this one, so a
 * late-running cleanup from a replaced component cannot silence the sink its
 * successor just installed.
 *
 * The sink receives two shapes, told apart by `resolved`: a fault, and the
 * notice that the same expression has since evaluated cleanly.
 *
 * @param {(report:
 *   | { fault: "expression-error", source: string | null, oid: string,
 *       label: string, expression: string, message: string }
 *   | { fault: "expression-error", resolved: true, source: string | null,
 *       oid: string, expression: string }
 * ) => void} next
 * @returns {() => void}
 */
export function setDesignFaultSink(next) {
    sink = next;
    return () => {
        if (sink === next) sink = null;
    };
}

/**
 * Reports one design fault. A no-op when no sink is installed, which is the
 * normal case everywhere but the preview.
 *
 * Never throws: this is called from inside a `catch` in the sandbox, and a sink
 * that fails must not turn a reported expression error into an unhandled one.
 */
export function reportDesignFault(fault) {
    deliver(fault);
}

/**
 * Reports that an expression evaluated without throwing, so a fault listed for
 * it can be dropped. Sent on every clean evaluation, not only after a fault:
 * this layer keeps no record of what it reported, and a panel that kept saying
 * "null has no toFixed" after the designer supplied the value would contradict
 * the form beneath it.
 *
 * @param {{ source?: string, oid?: string } | null} origin as passed to
 *   `evaluateExpressionSafe`
 * @param {string} expression
 */
export function reportDesignFaultResolved(origin, expression) {
    deliver({
        fault: "expression-error",
        resolved: true,
        source: origin?.source ?? null,
        oid: origin?.oid ?? "",
        expression,
    });
}

function deliver(report) {
    if (!sink) return;
    try {
        sink(report);
    } catch {
        // A broken sink is a preview-UI problem, not the form's. Swallowing it
        // keeps expression evaluation on its normal path.
    }
}

/**
 * Names the kind of expression for the designer, using the vocabulary the
 * Designer parent accepts (ADR-001 in the Viedoc.Designer repo). It is Clinic's
 * long-standing `ExpressionSourceEnum`, and Me maps onto it rather than the
 * reverse — it is the older of the two and the only one covering every kind.
 */
export const EXPRESSION_SOURCE = {
    FUNCTION: "Function",
    DEFAULT_VALUE: "Default value",
    ITEM_VISIBILITY: "Item visibility",
    ITEM_GROUP_VISIBILITY: "Item group visibility",
    EDIT_CHECK: "Edit check",
};
